import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useNoticeStore } from "../../store/useStore";
import PageHeader from "../../components/common/PageHeader";
import Table from "../../components/common/Table";
import Pagination from "../../components/common/Pagination";
import SortDropdown from "../../components/common/SortDropdown";

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #ffffff 0%, #fff8f2 100%);
`;

const ContentContainer = styled.div`
  padding: 32px 40px;
`;

const NoticeList = () => {
  const navigate = useNavigate();
  const {
    notices,
    currentPage,
    itemsPerPage,
    sortBy,
    isLoading,
    error,
    setCurrentPage,
    setSortBy,
    deleteNotice,
    getFilteredNotices,
    clearError,
  } = useNoticeStore();

  const sortOptions = [
    { value: "recent", label: "최근 생성일 순" },
    { value: "old", label: "오래된 생성일 순" },
    { value: "display", label: "사용자 표시 순" },
  ];

  const filteredNotices = useMemo(
    () => getFilteredNotices(),
    [notices, sortBy]
  );

  const paginatedNotices = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredNotices.slice(startIndex, endIndex);
  }, [filteredNotices, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredNotices.length / itemsPerPage);

  const columns = [
    {
      header: "No",
      key: "id",
      render: (value, row, index) =>
        (currentPage - 1) * itemsPerPage + index + 1,
    },
    {
      header: "공지사항 제목",
      key: "title",
    },
    {
      header: "공지사항 내용",
      key: "body",
      render: (value) =>
        value
          ? value.length > 50
            ? `${value.substring(0, 50)}...`
            : value
          : "",
    },
    {
      header: "등록 일자",
      key: "createdAt",
      render: (value) =>
        value ? new Date(value).toLocaleDateString("ko-KR") : "",
    },
    {
      header: "삭제",
      key: "actions",
    },
  ];

  const handleDelete = async (row) => {
    if (window.confirm("정말 삭제하시겠습니까?")) {
      const result = await deleteNotice(row.id);
      if (result.success) {
        alert("공지사항이 삭제되었습니다.");
      } else {
        alert(`삭제 실패: ${result.error}`);
      }
    }
  };

  const handleAddNotice = () => {
    navigate("/notice/new");
  };

  return (
    <PageContainer>
      <PageHeader
        title=""
        showAddButton={true}
        addButtonText="공지사항 등록"
        onAddClick={handleAddNotice}
        sortComponent={
          <SortDropdown
            options={sortOptions}
            value={sortBy}
            onChange={setSortBy}
          />
        }
      />

      <ContentContainer>
        {error && (
          <div
            style={{
              background: "#fee",
              color: "#d00",
              padding: "12px 16px",
              borderRadius: "8px",
              marginBottom: "20px",
              border: "1px solid #fcc",
            }}
          >
            {error}
            <button
              onClick={clearError}
              style={{
                marginLeft: "10px",
                background: "transparent",
                border: "none",
                color: "#d00",
                cursor: "pointer",
              }}
            >
              ✕
            </button>
          </div>
        )}

        {isLoading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <div>로딩 중...</div>
          </div>
        ) : (
          <>
            <Table
              columns={columns}
              data={paginatedNotices}
              onDelete={handleDelete}
            />

            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        )}
      </ContentContainer>
    </PageContainer>
  );
};

export default NoticeList;
